import { create } from 'zustand';
import type {
  AdminCartDto,
  AdminCartState,
  AdminCartSummaryDto,
  SavedListKind,
  SavedProductSaverDto,
  SavedProductSummaryDto,
} from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';

/**
 * What shoppers are holding (ADR-063): carts, wishlists and compare lists
 * across the store. Searched and paged on the server; nothing here filters
 * an in-memory list.
 */

export interface CartFilters {
  search: string;
  state: '' | AdminCartState;
  who: '' | 'customers' | 'guests';
}

export const EMPTY_CART_FILTERS: CartFilters = { search: '', state: '', who: '' };

interface SavedList {
  items: SavedProductSummaryDto[];
  cursor: string | null;
  total: number | null;
  search: string;
  loading: boolean;
}

const EMPTY_LIST: SavedList = { items: [], cursor: null, total: null, search: '', loading: false };

interface ShopperActivityState {
  summary: AdminCartSummaryDto | null;
  carts: AdminCartDto[];
  cartsCursor: string | null;
  cartsTotal: number | null;
  cartsLoading: boolean;
  cartFilters: CartFilters;

  saved: Record<SavedListKind, SavedList>;

  /** The accounts behind one product on a list, when its panel is open. */
  savers: { productId: string; kind: SavedListKind; items: SavedProductSaverDto[]; cursor: string | null; loading: boolean } | null;

  error: string | null;
  /** A reminder being sent, the last refusal, and one accepted but not delivered — shown in the cart's panel. */
  reminding: boolean;
  reminderError: string | null;
  reminderUndelivered: boolean;

  loadSummary: () => Promise<void>;
  /** The abandoned-cart message for one account's cart (ADR-066); the updated cart, or null when refused. */
  remind: (cartId: string, channel: 'EMAIL' | 'SMS' | 'WHATSAPP') => Promise<AdminCartDto | null>;
  clearReminderError: () => void;
  loadCarts: (filters?: Partial<CartFilters>) => Promise<void>;
  loadMoreCarts: () => Promise<void>;
  loadSaved: (kind: SavedListKind, search?: string) => Promise<void>;
  loadMoreSaved: (kind: SavedListKind) => Promise<void>;
  openSavers: (productId: string, kind: SavedListKind) => Promise<void>;
  loadMoreSavers: () => Promise<void>;
  closeSavers: () => void;
}

const PAGE_SIZE = 20;

export const useShopperActivityStore = create<ShopperActivityState>((set, get) => ({
  summary: null,
  carts: [],
  cartsCursor: null,
  cartsTotal: null,
  cartsLoading: false,
  cartFilters: EMPTY_CART_FILTERS,
  saved: { WISHLIST: EMPTY_LIST, COMPARE: EMPTY_LIST },
  savers: null,
  error: null,
  reminding: false,
  reminderError: null,
  reminderUndelivered: false,

  async remind(cartId, channel) {
    set({ reminding: true, reminderError: null, reminderUndelivered: false });
    try {
      const result = await withSession(() => adminApi.shopperActivity.remind(cartId, { channel }));
      let updated: AdminCartDto | null = null;
      set((state) => ({
        reminding: false,
        // Marked all the same, as the sweep marks a failed send: the message log says why.
        reminderUndelivered: !result.sent,
        carts: state.carts.map((cart) => {
          if (cart.id !== cartId) return cart;
          updated = { ...cart, recoveryMailedAt: result.remindedAt };
          return updated;
        }),
      }));
      return updated;
    } catch (error) {
      set({ reminding: false, reminderError: errorMessage(error, 'Could not send the reminder.') });
      return null;
    }
  },

  clearReminderError() {
    set({ reminderError: null, reminderUndelivered: false });
  },

  async loadSummary() {
    try {
      const summary = await withSession(() => adminApi.shopperActivity.cartSummary());
      set({ summary });
    } catch {
      // The cards read fine empty; the list is what matters.
      set({ summary: null });
    }
  },

  async loadCarts(filters) {
    const next = { ...get().cartFilters, ...filters };
    set({ cartsLoading: true, error: null, cartFilters: next });
    try {
      const page = await withSession(() => adminApi.shopperActivity.carts(cartQuery(next)));
      set({ carts: page.items, cartsCursor: page.meta.nextCursor, cartsTotal: page.meta.total ?? null, cartsLoading: false });
    } catch (error) {
      set({ cartsLoading: false, error: errorMessage(error, 'Could not load carts.') });
    }
  },

  async loadMoreCarts() {
    const { cartsCursor, cartsLoading, cartFilters, carts } = get();
    if (!cartsCursor || cartsLoading) return;
    set({ cartsLoading: true });
    try {
      const page = await withSession(() =>
        adminApi.shopperActivity.carts({ ...cartQuery(cartFilters), cursor: cartsCursor }),
      );
      set({ carts: [...carts, ...page.items], cartsCursor: page.meta.nextCursor, cartsLoading: false });
    } catch (error) {
      set({ cartsLoading: false, error: errorMessage(error, 'Could not load more carts.') });
    }
  },

  async loadSaved(kind, search) {
    const current = get().saved[kind];
    const term = search ?? current.search;
    set((state) => ({ error: null, saved: { ...state.saved, [kind]: { ...current, search: term, loading: true } } }));
    try {
      const page = await withSession(() =>
        adminApi.shopperActivity.savedProducts({ kind, limit: PAGE_SIZE, ...(term ? { search: term } : {}) }),
      );
      set((state) => ({
        saved: {
          ...state.saved,
          [kind]: { items: page.items, cursor: page.meta.nextCursor, total: page.meta.total ?? null, search: term, loading: false },
        },
      }));
    } catch (error) {
      set((state) => ({
        error: errorMessage(error, 'Could not load the saved lists.'),
        saved: { ...state.saved, [kind]: { ...state.saved[kind], loading: false } },
      }));
    }
  },

  async loadMoreSaved(kind) {
    const current = get().saved[kind];
    if (!current.cursor || current.loading) return;
    set((state) => ({ saved: { ...state.saved, [kind]: { ...current, loading: true } } }));
    try {
      const page = await withSession(() =>
        adminApi.shopperActivity.savedProducts({
          kind,
          limit: PAGE_SIZE,
          cursor: current.cursor ?? undefined,
          ...(current.search ? { search: current.search } : {}),
        }),
      );
      set((state) => ({
        saved: {
          ...state.saved,
          [kind]: { ...current, items: [...current.items, ...page.items], cursor: page.meta.nextCursor, loading: false },
        },
      }));
    } catch (error) {
      set((state) => ({
        error: errorMessage(error, 'Could not load more.'),
        saved: { ...state.saved, [kind]: { ...state.saved[kind], loading: false } },
      }));
    }
  },

  async openSavers(productId, kind) {
    set({ savers: { productId, kind, items: [], cursor: null, loading: true } });
    try {
      const page = await withSession(() => adminApi.shopperActivity.savers(productId, { kind, limit: PAGE_SIZE }));
      set({ savers: { productId, kind, items: page.items, cursor: page.meta.nextCursor, loading: false } });
    } catch (error) {
      set({ savers: { productId, kind, items: [], cursor: null, loading: false }, error: errorMessage(error, 'Could not load who saved it.') });
    }
  },

  async loadMoreSavers() {
    const savers = get().savers;
    if (!savers?.cursor || savers.loading) return;
    set({ savers: { ...savers, loading: true } });
    try {
      const page = await withSession(() =>
        adminApi.shopperActivity.savers(savers.productId, { kind: savers.kind, limit: PAGE_SIZE, cursor: savers.cursor ?? undefined }),
      );
      set({ savers: { ...savers, items: [...savers.items, ...page.items], cursor: page.meta.nextCursor, loading: false } });
    } catch (error) {
      set({ savers: { ...savers, loading: false }, error: errorMessage(error, 'Could not load more.') });
    }
  },

  closeSavers() {
    set({ savers: null });
  },
}));

function cartQuery(filters: CartFilters) {
  return {
    limit: PAGE_SIZE,
    ...(filters.search ? { search: filters.search } : {}),
    ...(filters.state ? { state: filters.state } : {}),
    ...(filters.who ? { who: filters.who } : {}),
  };
}
